import React, { useCallback, useEffect, useMemo, useState } from "react";
import NewFilterContainer from "../../../../components/NewFilterContainer ";
import MainPageContainer from "../../../../components/MainPageContainer";
import SecondaryHeader from "../../../Rajasthan/Demand/SecondaryHeader";
import Loading from "../../../../components/Loading";
import { formatDateForAPI } from "../../../Rajasthan/Utility/const";
import { useSelector, useDispatch } from "react-redux";
import { setLoadingFilterName } from "../../../../store/state/FilterLoadingState";
import {
  getPowerExchangeAnalysisData,
  getAvailableEntities,
  PowerExchangeAnalysisData,
  PowerExchangeAnalysisParams,
  PowerExchangeAnalysisSummary,
  AvailableEntity,
} from "../../../../Rest_api/RestApi";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import { renderHourTick, renderQuarterTick } from "../../../../components/recharts/components";
import {
  ChartTable,
} from "../../../../components/table/SummaryTable";
import LegendSummarySlotTable from "../../../../components/table/SummaryTableSlot";
import { BrushStart, ChartType, LegendKey } from "../../../../models/chart_model";
import { AddDatesWithFileName } from "../../../../helpers/helper";
import FilterMenu from "../../../../components/FilterMenu";
import { ExchangeDataTabs } from "../../../Rajasthan/Demand/Data";
import BlockRangeSelector from "../../../../components/BlockRangeSelector/BlockRangeSelector";
import { SearchBox } from "../../../../components/Search";
import { ExchangeColors } from "../../Exchange3/FormatData";
import { getTimeRange } from "../../../Dashboard/Exchange3/Chart";
import { BarChart3, Wallet } from "lucide-react";
import { getColor } from "../../../../utility/Colors";

const DataTabs = ["comparative", "cumulative"];

// Helper function to format cost in crores, lakhs, and thousands
const formatCostTooltip = (value: number): string => {
  if (value === 0) return "0";

  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "₹";

  // Crores (1 crore = 1,00,00,000)
  if (absValue >= 10000000) {
    const crores = absValue / 10000000;
    return `${sign}${crores.toFixed(2)} Cr`;
  }
  // Lakhs (1 lakh = 1,00,000)
  else if (absValue >= 100000) {
    const lakhs = absValue / 100000;
    return `${sign}${lakhs.toFixed(2)} Lakhs`;
  }
  // Thousands
  else if (absValue >= 1000) {
    const thousands = absValue / 1000;
    return `${sign}${thousands.toFixed(2)}K`;
  }
  // Less than 1000
  return `${sign}${absValue.toFixed(2)}`;
};

// Helper function to format cost for y-axis in lakhs
const formatCostAxis = (value: number): string => {
  if (value === 0) return "0";
  const lakhs = value / 100000;
  return `${lakhs.toFixed(1)}L`;
};

const chartSuffixes = [
  {
    suffix: "cost",
    displayName: "Cost",
    stackSuffix: "cost",
    chartType: ChartType.Line,
    unit: "Rs",
    yAxisId: "right",
  },
  {
    suffix: "price",
    displayName: "Price (Rs/kWh)",
    stackSuffix: "price",
    chartType: ChartType.Line,
    unit: "Rs/kWh",
    yAxisId: "right",
  },
  {
    suffix: "amount",
    displayName: "Volume",
    stackSuffix: "amount",
    chartType: ChartType.Bar,
    unit: "MW/MWh",
    yAxisId: "left",
  },
];

// Type filters: Drawal and Injection
const typeFilters = [
  { value: "Drawal", label: "Drawal" },
  { value: "Injection", label: "Injection" },
];

// Variable filters: Only Cost and Price (Volume is always selected, not shown in filter)
const variableFilterMetrics = chartSuffixes.filter(
  (metric) => metric.suffix !== "amount"
);

let Intervals = [
  {
    label: "Time Slot",
    key: "slot" as const,
    values: ["x_axis", "second_x_axis"],
    isTime: true,
  },
  {
    label: "Date",
    key: "day" as const,
    values: ["x_axis"],
    isTime: false,
  },
  {
    label: "Week",
    key: "week" as const,
    values: ["x_axis", "second_x_axis"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month" as const,
    values: ["x_axis"],
    isTime: false,
  },
];

const SLOT_INTERVAL =
  Intervals.find((interval) => interval.key === "slot") || Intervals[0];

const UNIT_OPTIONS = ["MW", "MWh"];

const exchanges = ["IEX", "PXIL", "HPX"];
const products = ["DAM", "RTM", "GDAM", "HPDAM"];

const PowerExchangeAnalysis = () => {
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const dataTab = useSelector((state: any) => state.viewMode.activeView);

  // Load persisted filters from localStorage
  const loadPersistedFilters = () => {
    try {
      const persisted = localStorage.getItem('powerExchangeAnalysisFilters');
      if (persisted) {
        return JSON.parse(persisted);
      }
    } catch (error) {
      console.error("Error loading persisted filters:", error);
    }
    return null;
  };

  // Save filters to localStorage
  const savePersistedFilters = (filters: any) => {
    try {
      localStorage.setItem('powerExchangeAnalysisFilters', JSON.stringify(filters));
    } catch (error) {
      console.error("Error saving persisted filters:", error);
    }
  };

  const persistedFilters = loadPersistedFilters();

  const [activefunctionTab, setActivefunctionTab] = useState(
    persistedFilters?.activefunctionTab || DataTabs[1]
  );
  const [rawData, setRawData] = useState<PowerExchangeAnalysisData[]>([]);
  const [summaryNumbers, setSummaryNumbers] = useState<PowerExchangeAnalysisSummary>({
    total_drawal_cost: 0,
    total_drawal_volume_mu: 0,
    total_injection_cost: 0,
    total_injection_volume_mu: 0,
  });
  const [chartData, setChartData] = useState<any[]>([]);
  const [summaryIntervalData, setSummaryIntervalData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedInterval, setSelectedInterval] = useState(
    persistedFilters?.selectedInterval || Intervals[0]
  );
  const [selectedUnit, setSelectedUnit] = useState(
    persistedFilters?.selectedUnit || UNIT_OPTIONS[0]
  );
  const [selectedBlock, setSelectedBlock] = useState<any[]>(
    persistedFilters?.selectedBlock || []
  );
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(
    persistedFilters?.selectedTypes || ["Drawal", "Injection"]
  );
  // Volume is always selected (not shown in filter)
  const volumeMetric = chartSuffixes.find((m) => m.suffix === "amount");

  const [selectedVariableMetrics, setSelectedVariableMetrics] = useState<any[]>([
    volumeMetric, // Volume always included
    variableFilterMetrics[0], // Cost by default (only one of Cost/Price can be selected)
  ]);
  const [loading, setLoading] = useState(false);
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>(
    persistedFilters?.selectedExchanges || ["IEX"]
  );
  const [selectedProducts, setSelectedProducts] = useState<string[]>(
    persistedFilters?.selectedProducts || ["DAM", "RTM"]
  );
  const [selectedEntities, setSelectedEntities] = useState<string[]>(
    persistedFilters?.selectedEntities || []
  );
  const [availableEntities, setAvailableEntities] = useState<AvailableEntity[]>([]);
  const dispatch = useDispatch();

  // Fetch available entities based on date range
  const fetchEntities = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      return;
    }

    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const entities = await getAvailableEntities({
        start_date: startDateStr,
        end_date: endDateStr,
      });

      setAvailableEntities(entities);

      // Set default entity selection if none selected
      setSelectedEntities((prevSelected) => {
        if (prevSelected.length === 0 && entities.length > 0) {
          // Select WBSETCL by default if available, otherwise first entity
          const wbsetclEntity = entities.find((e) => e.name === "WBSETCL");
          return wbsetclEntity ? [wbsetclEntity.name] : [entities[0].name];
        } else if (prevSelected.length > 0) {
          // Filter out entities that are no longer available
          const validEntities = prevSelected.filter((entityName) =>
            entities.some((e) => e.name === entityName)
          );
          if (validEntities.length !== prevSelected.length) {
            return validEntities;
          }
        }
        return prevSelected;
      });
    } catch (error) {
      console.error("Error fetching available entities:", error);
    }
  }, [DefaultDates, activeTab]);

  const fetchData = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      return;
    }

    setLoading(true);
    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      // Always use filters if selected - send comma-separated for multiple
      const exchangeName =
        selectedExchanges.length === exchanges.length
          ? undefined  // All selected, don't filter
          : selectedExchanges.length > 0
            ? selectedExchanges.join(",")
            : undefined;

      const productName =
        selectedProducts.length === products.length
          ? undefined  // All selected, don't filter
          : selectedProducts.length > 0
            ? selectedProducts.join(",")
            : undefined;

      // Send comma-separated for multiple entities
      const entityName =
        selectedEntities.length > 0
          ? selectedEntities.join(",")
          : undefined;

      const params: PowerExchangeAnalysisParams = {
        interval: selectedInterval.key,
        function: activefunctionTab as "comparative" | "cumulative",
        start_date: startDateStr,
        end_date: endDateStr,
      };

      if (exchangeName) {
        params.exchange_name = exchangeName;
      }
      if (productName) {
        params.product_name = productName;
      }
      if (entityName) {
        params.entity_name = entityName;
      }

      const response = await getPowerExchangeAnalysisData(params);

      setRawData(response.data);
      setSummaryNumbers(response.summary);

      if (selectedInterval.key === "slot") {
        setUniqueBlock(
          Array.from(
            new Set(response.data.map((item: PowerExchangeAnalysisData) => item.x_axis))
          )
        );
      } else {
        setUniqueBlock([]);
      }
    } catch (error) {
      console.error("Error fetching power exchange analysis data:", error);
      setRawData([]);
    } finally {
      setLoading(false);
      dispatch(setLoadingFilterName(""));
    }
  }, [
    DefaultDates,
    activeTab,
    selectedInterval.key,
    activefunctionTab,
    selectedExchanges,
    selectedProducts,
    selectedEntities,
  ]);

  // Fetch entities when date range changes
  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save filters to localStorage when they change
  useEffect(() => {
    const filters = {
      selectedInterval,
      selectedUnit,
      selectedBlock,
      selectedTypes,
      selectedExchanges,
      selectedProducts,
      selectedEntities,
      activefunctionTab,
    };
    savePersistedFilters(filters);
  }, [
    selectedInterval,
    selectedUnit,
    selectedBlock,
    selectedTypes,
    selectedExchanges,
    selectedProducts,
    selectedEntities,
    activefunctionTab,
  ]);

  // Save filters to localStorage when they change
  useEffect(() => {
    const filters = {
      selectedInterval,
      selectedUnit,
      selectedBlock,
      selectedTypes,
      selectedExchanges,
      selectedProducts,
      selectedEntities,
      activefunctionTab,
    };
    savePersistedFilters(filters);
  }, [
    selectedInterval,
    selectedUnit,
    selectedBlock,
    selectedTypes,
    selectedExchanges,
    selectedProducts,
    selectedEntities,
    activefunctionTab,
  ]);

  const processDataForInterval = useCallback(
    (sourceData: PowerExchangeAnalysisData[], intervalConfig: (typeof Intervals)[number]) => {
      let filteredData = [...sourceData];

      // Apply filters - in cumulative mode, backend already filters, so skip frontend filtering
      // (cumulative mode returns null for exchange/entity/product fields)
      if (activefunctionTab === "comparative") {
        if (
          selectedExchanges.length > 0 &&
          selectedExchanges.length < exchanges.length
        ) {
          filteredData = filteredData.filter((item) =>
            item.exchange__name && selectedExchanges.includes(item.exchange__name)
          );
        }

        if (
          selectedProducts.length > 0 &&
          selectedProducts.length < products.length
        ) {
          filteredData = filteredData.filter((item) =>
            item.product__name && selectedProducts.includes(item.product__name)
          );
        }

        if (selectedEntities.length > 0) {
          filteredData = filteredData.filter((item) =>
            item.entity__name && selectedEntities.includes(item.entity__name)
          );
        }
      }

      if (selectedBlock.length > 0 && intervalConfig.key === "slot") {
        filteredData = filteredData.filter((item) =>
          selectedBlock.map((block) => block.value).includes(item.x_axis)
        );
      }

      const groupedData: any = {};

      filteredData.forEach((item) => {
        let key: string;
        if ((intervalConfig.isTime || intervalConfig.values.includes("second_x_axis")) && item.second_x_axis) {
          key = `${item.second_x_axis}_${item.x_axis}`;
        } else {
          key = item.x_axis;
        }

        if (!groupedData[key]) {
          groupedData[key] = {
            x_axis: item.x_axis,
            second_x_axis: item.second_x_axis,
          };
        }

        const typeName = item.type__name || "";
        const exchangeName = item.exchange__name || "";
        const entityName = item.entity__name || "";
        const productName = item.product__name || "";

        selectedVariableMetrics.forEach((typeMetric) => {
          let actualSuffix = typeMetric.suffix;
          if (typeMetric.unit === "MW/MWh") {
            actualSuffix = `${typeMetric.suffix}_${selectedUnit.toLowerCase()}`;
          }

          // For price: group only by exchange and product (not by type or entity)
          // For cost and amount: group by type, exchange, entity, product (or just type in cumulative)
          let prefix: string;
          if (typeMetric.suffix === "price") {
            // Price: only by exchange and product
            if (activefunctionTab === "cumulative") {
              prefix = ""; // Cumulative: single price line
            } else {
              prefix = exchangeName && productName
                ? `${exchangeName}_${productName}`
                : "";
            }
          } else {
            // Cost and Amount: by type, exchange, entity, product
            prefix = activefunctionTab === "cumulative"
              ? `${typeName}`
              : exchangeName && entityName && productName
                ? `${typeName}_${exchangeName}_${entityName}_${productName}`
                : `${typeName}`;
          }

          const dataKey = prefix ? `${prefix}_${actualSuffix}` : actualSuffix;

          if (typeMetric.suffix === "amount") {
            const value = selectedUnit === "MW" ? item.amount_mw : item.amount_mwh;
            groupedData[key][dataKey] = value;
          } else if (typeMetric.suffix === "cost") {
            // Convert cost to lakhs for y-axis display (1 lakh = 100,000)
            groupedData[key][dataKey] = item.cost / 100000;
            // Store original cost value for tooltip
            groupedData[key][`${dataKey}_original`] = item.cost;
          } else if (typeMetric.suffix === "price") {
            // Price is already in Rs/kWh, use as is
            // For price, we need to handle multiple types (Drawal/Injection) with same exchange/product
            // Since price is same for both, we can use any one
            if (!groupedData[key][dataKey]) {
              groupedData[key][dataKey] = item.wt_avg_price || 0;
            }
          }
        });
      });

      const formattedChartData = Object.values(groupedData);

      // Generate keys based on all selected metrics
      // For price: keys are exchange_product (or empty for cumulative)
      // For cost/amount: keys are type_exchange_entity_product (or type for cumulative)
      const allKeys = new Set<string>();

      selectedVariableMetrics.forEach((typeMetric) => {
        if (typeMetric.suffix === "price") {
          // Price keys: exchange_product (or empty for cumulative)
          if (activefunctionTab === "cumulative") {
            allKeys.add(""); // Single key for cumulative price
          } else {
            filteredData
              .filter((item) => item.exchange__name && item.product__name)
              .forEach((item) => {
                allKeys.add(`${item.exchange__name}_${item.product__name}`);
              });
          }
        } else {
          // Cost and Amount keys: type_exchange_entity_product (or type for cumulative)
          if (activefunctionTab === "cumulative") {
            filteredData.forEach((item) => {
              if (item.type__name) {
                allKeys.add(item.type__name);
              }
            });
          } else {
            filteredData.forEach((item) => {
              const typeName = item.type__name || "";
              const exchangeName = item.exchange__name || "";
              const entityName = item.entity__name || "";
              const productName = item.product__name || "";
              if (exchangeName && entityName && productName) {
                allKeys.add(`${typeName}_${exchangeName}_${entityName}_${productName}`);
              } else if (typeName) {
                allKeys.add(typeName);
              }
            });
          }
        }
      });

      const keys = Array.from(allKeys);

      return {
        chartData: formattedChartData,
        tableData: filteredData,
        chartKeys: keys,
      };
    },
    [
      activefunctionTab,
      selectedBlock,
      selectedExchanges,
      selectedProducts,
      selectedEntities,
      selectedTypes,
      selectedVariableMetrics,
      selectedUnit,
    ]
  );

  useEffect(() => {
    if (!rawData.length) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      setSummaryIntervalData([]);
      return;
    }

    const processed = processDataForInterval(rawData, selectedInterval);
    setChartData(processed.chartData);
    setChartKeys(processed.chartKeys);
    setTableData(processed.tableData);
    setSummaryIntervalData(processed.chartData);
  }, [
    rawData,
    selectedInterval,
    processDataForInterval,
  ]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    yAxisId: string
  ) => {
    let actualUnit = unit;
    if (unit === "MW/MWh") {
      actualUnit = selectedUnit;
    }
    const legendIndexMapping: Record<string, number> = {
      "Drawal Volume": 1,
      "Injection Volume": 3,
    }

    const legendName = `${key.replace(/_/g, " ")} ${displayName}`;
    const colorIndex = legendIndexMapping[legendName];
    const hasColorMapping = colorIndex !== undefined;

    return {
      dataKey: key ? `${key}_${suffix}` : suffix,
      name: legendName,
      stroke: hasColorMapping ? getColor(colorIndex) : undefined,
      type: chartType,
      unit: actualUnit,
      yAxisId: yAxisId,
      stackId:
        key && key.includes("Drawal") ? "drawal" : key && key.includes("Injection") ? "injection" : undefined,
    };
  };

  function getLegends() {
    const legends: any[] = [];
    const hasPrice = selectedVariableMetrics.some((m) => m.suffix === "price");

    chartKeys.forEach((key, index) => {
      // For price: keys are "Exchange_Product" or "" (cumulative), no type filtering needed
      // For cost/amount: keys are "TypeName" or "TypeName_Exchange_Entity_Product", filter by type
      if (!hasPrice) {
        // Extract type name from key (key format: "TypeName" or "TypeName_Exchange_Entity_Product")
        const keyType = key.split("_")[0];

        // Filter by selected types - only include legends for selected types
        if (selectedTypes.length > 0 && !selectedTypes.includes(keyType)) {
          return; // Skip this key if its type is not selected
        }
      }

      selectedVariableMetrics.forEach((typeMetric) => {
        let actualSuffix = typeMetric.suffix;
        if (typeMetric.unit === "MW/MWh") {
          actualSuffix = `${typeMetric.suffix}_${selectedUnit.toLowerCase()}`;
        }

        // Calculate the dataKey that would be used
        const dataKey = key ? `${key}_${actualSuffix}` : actualSuffix;

        // Check if this dataKey exists in chartData (has actual data)
        const hasData = chartData.some((dataPoint) => {
          const value = dataPoint[dataKey];
          return value !== undefined && value !== null && !isNaN(parseFloat(value));
        });

        // Only create legend if data exists
        if (hasData) {
          legends.push(
            createChartObject(
              key,
              index,
              actualSuffix,
              typeMetric.displayName,
              typeMetric.stackSuffix,
              typeMetric.chartType,
              typeMetric.unit,
              typeMetric.yAxisId
            )
          );
        }
      });
    });

    return legends;
  }

  const memoizedLegends = useMemo(
    () => getLegends(),
    [chartKeys, selectedVariableMetrics, selectedUnit, selectedTypes, chartData]
  );


  const costLegends = useMemo(
    () =>
      memoizedLegends.map((legend: LegendKey) =>
        legend.unit === "Rs" ? legend : null
      ),
    [memoizedLegends]
  );

  // Use "Rs(Lakhs)" for cost/price display in Summary table
  const summaryLegends = useMemo(
    () =>
      memoizedLegends.map((legend: LegendKey) =>
        legend.unit === "Rs" ? { ...legend, unit: "Rs(Lakhs)" } : legend
      ),
    [memoizedLegends]
  );

  const xDataKey = "x_axis";

  const viewExtraKeys = selectedInterval.isTime
    ? ["x_axis", "second_x_axis"]
    : ["x_axis"];

  const viewExtraLabels = selectedInterval.isTime
    ? ["Time Slot", selectedInterval.key === "slot" ? "Date" : "Month"]
    : ["Date"];

  const summaryExtraKeys = selectedInterval.isTime
    ? ["x_axis", "second_x_axis"]
    : ["x_axis"];

  const summaryExtraLabels = selectedInterval.isTime
    ? ["Time Slot", selectedInterval.key === "slot" ? "Date" : "Month"]
    : ["Date"];

  const tableUnits: Record<string, string> = {};
  chartSuffixes.forEach((curr) => {
    let actualUnit = curr.unit;
    if (curr.unit === "MW/MWh") {
      actualUnit = selectedUnit;
    }
    tableUnits[curr.suffix] = actualUnit;
  });

  const tablePar = {
    legends: summaryLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    extraKeys: viewExtraKeys,
    extraLabels: viewExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Entity Analysis",
      dates: DefaultDates[activeTab]?.defaultRangeDate,
    }),
    customUnits: tableUnits,
    showTotal: false
  };

  function secondDataKey() {
    if (selectedInterval.values.includes("second_x_axis")) {
      return "second_x_axis";
    } else {
      return undefined;
    }
  }

  return (
    <>
      <div className="gas-page-container">
        {loading && <Loading />}
        <NewFilterContainer>
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="Interval"
              items={Intervals.map((item) => item.label)}
              selectedItems={[selectedInterval.label]}
              onSelect={(item, index) => {
                setSelectedInterval(Intervals[index]);
                setSelectedBlock([]);
              }}
            />

            <FilterMenu
              title="Unit"
              items={UNIT_OPTIONS}
              selectedItems={[selectedUnit]}
              onSelect={(item, index) => {
                setSelectedUnit(UNIT_OPTIONS[index]);
              }}
            />

            <FilterMenu
              title="Function"
              items={DataTabs.map(tab => tab.charAt(0).toUpperCase() + tab.slice(1))}
              selectedItems={[activefunctionTab.charAt(0).toUpperCase() + activefunctionTab.slice(1)]}
              onSelect={(item, index) => {
                setActivefunctionTab(DataTabs[index]);
              }}
            />

            <FilterMenu
              title="Exchange"
              items={exchanges}
              selectedItems={selectedExchanges}
              setSelectedItems={(items: string[]) => {
                setSelectedExchanges(items);
              }}
            />

            <FilterMenu
              title="Product"
              items={products}
              selectedItems={selectedProducts}
              setSelectedItems={(items: string[]) => {
                setSelectedProducts(items);
              }}
            />

            <FilterMenu
              title="Type"
              items={typeFilters.map((item) => item.label)}
              selectedItems={selectedTypes}
              setSelectedItems={(items: string[]) => {
                setSelectedTypes(items);
              }}
            />

            <FilterMenu
              title="Variable"
              items={variableFilterMetrics.map((item) => item.displayName)}
              selectedItems={selectedVariableMetrics
                .filter((m) => m.suffix !== "amount")
                .map((item) => item.displayName)}
              setSelectedItems={(selectedItems: string[]) => {
                if (!Array.isArray(selectedItems)) {
                  return;
                }

                // Only allow one selection at a time (Cost or Price)
                // If a new item is selected, replace the previous one
                let newSelectedMetric;
                if (selectedItems.length > 0) {
                  // Get the last selected item (most recent selection)
                  const lastSelected = selectedItems[selectedItems.length - 1];
                  newSelectedMetric = variableFilterMetrics.find(
                    (item) => item.displayName === lastSelected
                  );
                } else {
                  // If nothing selected, default to Cost
                  newSelectedMetric = variableFilterMetrics[0];
                }

                // Always include volume metric + the selected Cost/Price
                if (newSelectedMetric) {
                  setSelectedVariableMetrics([volumeMetric, newSelectedMetric]);
                }
              }}
            />
          </div>
        </NewFilterContainer>

        <MainPageContainer>
          <SecondaryHeader ExchangeDataTabs={ExchangeDataTabs} />
          <div className="gas-chart-container">
            <div className="gas-heading gap-x-10">
              <h2 className="chartHeading w-60">Power Exchange Analysis</h2>
              <div className="flex gap-x-6 justify-end items-center">
                <div className="w-80">
                  {/* <label className="block text-sm font-medium text-gray-700 mb-1">
                    Entity
                  </label> */}
                  <SearchBox
                    options={availableEntities.map((e) => ({
                      value: e.name,
                      label: e.name,
                    }))}
                    selectedOptions={selectedEntities.map((name) => ({
                      value: name,
                      label: name,
                    }))}
                    onChange={(selected: any) => {
                      if (Array.isArray(selected)) {
                        setSelectedEntities(
                          selected.map((option: any) => option.value as string)
                        );
                      } else {
                        setSelectedEntities([]);
                      }
                    }}
                    placeholder="Select Entity"
                    isMany={true}
                  />
                </div>
                {selectedInterval.key === "slot" && (
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Select Slot"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                )}
              </div>
            </div>

            {/* Summary Numbers */}
            {dataTab.label === "Chart" && (
              <div className="mb-6 bg-white rounded-xl p-4 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Total Drawal Cost */}
                  <div className="flex flex-row justify-start items-center gap-4 bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-2xl transform hover:scale-105 transition duration-300 border border-orange-200 shadow-sm">
                    <div className="bg-orange-500 p-3 rounded-xl">
                      <Wallet className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm text-gray-600 mb-1 font-medium">
                        Total Drawal Cost
                      </h3>
                      <p className="text-xl font-bold text-gray-900">
                        {formatCostTooltip(summaryNumbers.total_drawal_cost)}
                      </p>
                    </div>
                  </div>

                  {/* Total Drawal Volume */}
                  <div className="flex flex-row justify-start items-center gap-4 bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-2xl transform hover:scale-105 transition duration-300 border border-purple-200 shadow-sm">
                    <div className="bg-purple-500 p-3 rounded-xl">
                      <BarChart3 className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm text-gray-600 mb-1 font-medium">
                        Total Drawal Volume
                      </h3>
                      <p className="text-xl font-bold text-gray-900">
                        {summaryNumbers.total_drawal_volume_mu.toFixed(2)}{" "}
                        <span className="text-base text-gray-600 font-normal">MU</span>
                      </p>
                    </div>
                  </div>

                  {/* Total Injection Cost */}
                  <div className="flex flex-row justify-start items-center gap-4 bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-2xl transform hover:scale-105 transition duration-300 border border-green-200 shadow-sm">
                    <div className="bg-green-500 p-3 rounded-xl">
                      <Wallet className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm text-gray-600 mb-1 font-medium">
                        Total Injection Cost
                      </h3>
                      <p className="text-xl font-bold text-gray-900">
                        {formatCostTooltip(summaryNumbers.total_injection_cost)}
                      </p>
                    </div>
                  </div>

                  {/* Total Injection Volume */}
                  <div className="flex flex-row justify-start items-center gap-4 bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-2xl transform hover:scale-105 transition duration-300 border border-blue-200 shadow-sm">
                    <div className="bg-blue-500 p-3 rounded-xl">
                      <BarChart3 className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm text-gray-600 mb-1 font-medium">
                        Total Injection Volume
                      </h3>
                      <p className="text-xl font-bold text-gray-900">
                        {summaryNumbers.total_injection_volume_mu.toFixed(2)}{" "}
                        <span className="text-base text-gray-600 font-normal">MU</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {dataTab.label === "Chart" && (
              <ReMixChart
                data={chartData}
                showBrush={chartData.length > 96 ? true : undefined}
                brushStart={BrushStart.Start}
                isTimeSlot={selectedInterval.isTime}
                onlyTitle={true}
                legends={memoizedLegends}
                // secondYAxisDomain={[0, 30000000]}
                secondYAxisLabel={
                  selectedVariableMetrics.some(m => m.suffix === "price")
                    ? "₹/kWh"
                    : selectedVariableMetrics.some(m => m.suffix === "cost")
                      ? "₹ (Lakhs)"
                      : undefined
                }
                yAxisLabel={selectedUnit}
                xDataKey={xDataKey}
                xTick={
                  selectedInterval.key === "slot" && selectedBlock.length === 0
                    ? (value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }
                    : selectedInterval.key === "week"
                      ? renderQuarterTick
                      : undefined
                }
                secondXDataKey={secondDataKey()}
                customTooltip={(params: any) => {
                  const param = Array.isArray(params) ? params[0] : params;
                  let items = Array.isArray(params) ? params : [params];

                  let title = param.name;
                  if (secondDataKey() && chartData[param.dataIndex]?.[secondDataKey()!]) {
                    if (selectedInterval.isTime) {
                      title = `${chartData[param.dataIndex][secondDataKey()!]} - ${param.name
                        } (${getTimeRange(parseInt(param.name))})`;
                    } else {
                      title = `${chartData[param.dataIndex][secondDataKey()!]} - ${param.name
                        }`;
                    }
                  } else if (selectedInterval.isTime) {
                    title = getTimeRange(parseInt(param.name)) + ` (${param.name})`;
                  }

                  const dataPoint = chartData[param.dataIndex];

                  const tooltipItems = items
                    .filter((item: any) => {
                      const value = parseFloat(item.value);
                      return !isNaN(value) && isFinite(value);
                    })
                    .map((item: any) => {
                      // Find the legend to get the dataKey
                      const legend = memoizedLegends.find((l) => l.name === item.seriesName);
                      const dataKey = legend?.dataKey || "";

                      // Check if this is a cost value and get original value
                      let displayValue: number = parseFloat(item.value);
                      if (dataKey.includes("cost") && dataPoint) {
                        // Try to find the original value
                        const originalKey = `${dataKey}_original`;

                        if (originalKey in dataPoint) {
                          displayValue = dataPoint[originalKey];
                        } else {
                          // Convert back from lakhs to original (value shown is in lakhs)
                          displayValue = displayValue * 100000;
                        }
                      }
                      // Price is already in Rs/kWh, no conversion needed

                      // Format based on unit
                      let formattedValue: string;
                      if (legend?.unit === "Rs") {
                        formattedValue = formatCostTooltip(displayValue);
                      } else if (legend?.unit === "Rs/kWh") {
                        // Price: show with 2 decimal places
                        formattedValue = displayValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                      } else {
                        formattedValue = displayValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                      }

                      const bgColor = item.color;
                      const unit = legend?.unit || "";
                      const displaySeriesName = item.seriesName;

                      return `
                        <div class="tooltip-item" data-name="${displaySeriesName?.toLowerCase()}">
                          <div class="color-indicator" style="background-color: ${bgColor}"></div>
                          <div class="series-name">${displaySeriesName}</div>
                          <div class="value">${formattedValue}${unit ? ` ${unit.replace("Rs", "").replace("/kWh", "₹/kWh")}` : ""}</div>
                        </div>
                      `;
                    });

                  const tooltipId = `tooltip-${Date.now()}`;
                  const fontSize = 12;

                  return `
                    <div class="enhanced-tooltip" id="${tooltipId}">
                      <div class="tooltip-header">
                        <div class="tooltip-title">${title}</div>
                      </div>
                      
                      <div class="tooltip-content">
                        ${tooltipItems.join("")}
                      </div>
                    </div>
                    
                    <style>
                      .enhanced-tooltip {
                        font-family: 'Inter', system-ui, sans-serif;
                        width: 100%;
                        min-width: 200px;
                      }
                      .tooltip-header {
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                        justify-content: start;
                        align-items: start;
                        border-bottom: 1px solid #eaeaea;
                        padding-bottom: 8px;
                        margin-bottom: 8px;
                      }
                      .tooltip-title {
                        font-weight: 600;
                        font-size: ${fontSize + 1}px;
                        color: #333;
                      }
                      .tooltip-content {
                        max-height: 300px;
                        overflow-y: auto;
                        padding-right: 10px;
                      }
                      .tooltip-item {
                        display: flex;
                        align-items: center;
                        padding: 4px 0;
                        border-bottom: 1px solid #f0f0f0;
                      }
                      .tooltip-item:last-child {
                        border-bottom: none;
                      }
                      .color-indicator {
                        width: 10px;
                        height: 10px;
                        border-radius: 50%;
                        margin-right: 8px;
                      }
                      .series-name {
                        flex: 1;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        font-size: ${fontSize}px;
                      }
                      .value {
                        font-weight: 500;
                        margin-left: 10px;
                        font-size: ${fontSize}px;
                      }
                    </style>
                  `;
                }}
              />
            )}
            {dataTab.label === "Summary" && (
              <LegendSummarySlotTable
                legends={summaryLegends}
                avgData={chartData}
                minMaxData={
                  summaryIntervalData.length > 0 ? summaryIntervalData : chartData
                }
                avgTableData={TableData}
                extraKeys={summaryExtraKeys}
                extraLabels={summaryExtraLabels}
                unit={selectedUnit}
                priceLegends={costLegends?.map((l) =>
                  l ? { ...l, unit: "Rs (Lakhs)" } : null
                )}
                volumeUnitKey={selectedUnit.toLowerCase()}
                block_key={selectedInterval.key === "slot" ? "x_axis" : undefined}
                showWtdAvg={false}
                showTotal={false}
              />
            )}
            {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </div>
          <div className="gna-footer-container flex justify-between">
            <p className="gna-footer-text col-span-1">
              Powered by <strong>GNA Energy</strong>
            </p>
            <p className="text-xs text-gray-500 text-center">
              * 2 Paisa per kWh is added as transaction fees for calculating cost.
            </p>

            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </MainPageContainer>
      </div>
    </>
  );
};

export default PowerExchangeAnalysis;

